import React, { useEffect, useState } from "react";
import { Sun, Moon, Volume2, VolumeX, Command } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { audioMuted, setAudioMuted } from "@/hooks/useAudioCue";

const formatNum = (n) => (n ?? 0).toLocaleString("en-IN");

const riskLabel = (r) => {
  if (r >= 0.8) return { label: "Critical", className: "sev-critical" };
  if (r >= 0.6) return { label: "High", className: "sev-high" };
  if (r >= 0.4) return { label: "Elevated", className: "sev-medium" };
  return { label: "Nominal", className: "sev-low" };
};

export const TopBar = ({ bootstrap, kpi, connected }) => {
  const { theme, toggle } = useTheme();
  const [clock, setClock] = useState(() => new Date());
  const [muted, setMuted] = useState(audioMuted());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    setAudioMuted(next);
  };

  const openPalette = () => window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));

  const stadium = bootstrap?.stadium;
  const phase = bootstrap?.current_phase;
  const occ = kpi?.total_occupancy ?? bootstrap?.kpis?.total_occupancy ?? 0;
  const risk = kpi?.composite_risk ?? bootstrap?.kpis?.composite_risk ?? 0;
  const cap = bootstrap?.kpis?.total_capacity ?? 0;
  const pct = cap ? Math.round((occ / cap) * 100) : 0;
  const { label: riskLbl, className: riskCls } = riskLabel(risk);

  return (
    <header
      className="h-14 border-b border-border bg-[hsl(var(--surface))] flex items-stretch px-3 shrink-0 overflow-hidden"
      data-testid="top-bar"
    >
      {/* Venue */}
      <div className="hidden md:flex items-center min-w-0 pr-3">
        <div className="min-w-0 whitespace-nowrap">
          <div className="font-display font-black tracking-tight text-xs leading-none truncate" data-testid="top-stadium-name">
            {stadium?.name ?? "Stadium"}
          </div>
          <div className="micro-label mt-1 truncate" data-testid="top-stadium-phase">
            {phase?.label ?? "—"}
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {/* Occupancy compact */}
      <div className="hidden lg:flex items-center gap-2 px-3 border-l border-border whitespace-nowrap">
        <div>
          <div className="micro-label">Occupancy</div>
          <div className="font-mono text-xs font-semibold leading-none mt-0.5" data-testid="top-occupancy">
            {formatNum(occ)} <span className="text-muted-foreground">· {pct}%</span>
          </div>
        </div>
      </div>

      {/* Risk compact */}
      <div className="flex items-center gap-2 px-3 border-l border-border whitespace-nowrap">
        <div>
          <div className="micro-label">Risk</div>
          <div className={`font-mono text-xs font-bold leading-none mt-0.5 ${riskCls}`} data-testid="top-risk">
            {(risk * 100).toFixed(0)} · {riskLbl}
          </div>
        </div>
      </div>

      {/* Clock */}
      <div className="hidden xl:flex items-center px-3 border-l border-border whitespace-nowrap">
        <div>
          <div className="micro-label">UTC</div>
          <div className="font-mono text-xs leading-none mt-0.5" data-testid="top-clock">
            {clock.toISOString().slice(11, 19)}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 pl-3 border-l border-border">
        <button onClick={toggle} className="btn-tac !px-2 !py-1.5" data-testid="theme-toggle-btn" aria-label="Toggle theme" title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}>
          {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
        </button>
        <button onClick={toggleMute} className="btn-tac !px-2 !py-1.5" data-testid="audio-mute-btn" aria-label="Toggle audio cue" title={muted ? "Unmute alerts" : "Mute alerts"}>
          {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button onClick={openPalette} className="btn-tac !px-2 !py-1.5 hidden md:inline-flex" data-testid="open-cmdk-btn" aria-label="Open command palette" title="Command palette (⌘K)">
          <Command className="w-4 h-4" />
          <span className="font-mono text-[10px]">⌘K</span>
        </button>
      </div>
    </header>
  );
};

export default TopBar;
